import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';

const ExpenseContext = createContext(null);

export function ExpenseProvider({ children }) {
  const { isReady, isAuthenticated } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [wallets, setWallets] = useState([]);
  // Distinguishes "budgets is [] because nothing has loaded yet" from "budgets
  // is [] because there really are none" - GoalContext needs that distinction
  // before it can safely treat a missing id as "this budget was deleted".
  const [budgetsLoaded, setBudgetsLoaded] = useState(false);
  const canFetch = isReady && isAuthenticated;

  useEffect(() => {
    if (!canFetch) {
      setExpenses([]);
      setBudgets([]);
      setWallets([]);
      setBudgetsLoaded(false);
      return undefined;
    }
    let cancelled = false;
    Promise.all([api.get('/expenses'), api.get('/budgets'), api.get('/wallets')])
      .then(([{ expenses: fetchedExpenses }, { budgets: fetchedBudgets }, { wallets: fetchedWallets }]) => {
        if (cancelled) return;
        setExpenses(fetchedExpenses);
        setBudgets(fetchedBudgets);
        setWallets(fetchedWallets);
        setBudgetsLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setExpenses([]);
        setBudgets([]);
        setWallets([]);
        setBudgetsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [canFetch]);

  const refreshBudgets = useCallback(async () => {
    const { budgets: refreshed } = await api.get('/budgets');
    setBudgets(refreshed);
  }, []);

  // Editing a wallet's balance (or creating one with an opening amount) makes the
  // backend record a matching transaction, so expenses needs to be re-pulled after
  // any wallet write - not just wallets after any expense write.
  const refreshExpenses = useCallback(async () => {
    const { expenses: refreshed } = await api.get('/expenses');
    setExpenses(refreshed);
  }, []);

  const refreshWallets = useCallback(async () => {
    const { wallets: refreshed } = await api.get('/wallets');
    setWallets(refreshed);
  }, []);

  const addExpense = useCallback(async (expense) => {
    const { expense: created } = await api.post('/expenses', expense);
    setExpenses((current) => [...current, created]);
    await refreshBudgets();
    await refreshWallets();
    return created;
  }, [refreshBudgets, refreshWallets]);

  const updateExpense = useCallback(async (updatedExpense) => {
    const { expense: updated } = await api.patch(`/expenses/${updatedExpense.id}`, updatedExpense);
    setExpenses((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    await refreshBudgets();
    await refreshWallets();
    return updated;
  }, [refreshBudgets, refreshWallets]);

  const deleteExpense = useCallback(async (id) => {
    await api.delete(`/expenses/${id}`);
    setExpenses((current) => current.filter((item) => item.id !== id));
    await refreshBudgets();
    await refreshWallets();
  }, [refreshBudgets, refreshWallets]);

  const addBudget = useCallback(async (budget) => {
    const { budget: created } = await api.post('/budgets', budget);
    setBudgets((current) => [...current, created]);
    return created;
  }, []);

  const updateBudget = useCallback(async (updatedBudget) => {
    const { budget: updated } = await api.patch(`/budgets/${updatedBudget.id}`, updatedBudget);
    setBudgets((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    return updated;
  }, []);

  const deleteBudget = useCallback(async (id) => {
    await api.delete(`/budgets/${id}`);
    setBudgets((current) => current.filter((item) => item.id !== id));
  }, []);

  // Adds a budget to local state WITHOUT calling the API - for one the server created
  // as a side effect of something else (a goal created with a savings budget attached).
  const adoptBudget = useCallback((budget) => {
    setBudgets((current) => (current.some((item) => item.id === budget.id) ? current : [...current, budget]));
  }, []);

  // Drops a budget from local state WITHOUT calling the API - for budgets the server
  // has already deleted as a side effect of something else (deleting a goal removes the
  // Saving budget it created). Calling deleteBudget here would 404 on a row that is
  // already gone.
  const forgetBudget = useCallback((id) => {
    setBudgets((current) => current.filter((item) => item.id !== id));
  }, []);

  const addWallet = useCallback(async (wallet) => {
    const { wallet: created } = await api.post('/wallets', wallet);
    setWallets((current) => [...current, created]);
    await refreshExpenses();
    return created;
  }, [refreshExpenses]);

  const updateWallet = useCallback(async (updatedWallet) => {
    const { wallet: updated } = await api.patch(`/wallets/${updatedWallet.id}`, updatedWallet);
    setWallets((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    await refreshExpenses();
    return updated;
  }, [refreshExpenses]);

  const deleteWallet = useCallback(async (id) => {
    await api.delete(`/wallets/${id}`);
    setWallets((current) => current.filter((item) => item.id !== id));
    // Deleted wallet's id lives on in any linked expenses (server sets walletId to
    // null via FK), so re-pull to drop the now-stale local references.
    await refreshExpenses();
  }, [refreshExpenses]);

  const value = useMemo(() => ({
    expenses, addExpense, updateExpense, deleteExpense,
    budgets, budgetsLoaded, addBudget, updateBudget, deleteBudget, adoptBudget, forgetBudget,
    wallets, addWallet, updateWallet, deleteWallet,
  }), [
    expenses, addExpense, updateExpense, deleteExpense,
    budgets, budgetsLoaded, addBudget, updateBudget, deleteBudget, adoptBudget, forgetBudget,
    wallets, addWallet, updateWallet, deleteWallet,
  ]);

  return <ExpenseContext.Provider value={value}>{children}</ExpenseContext.Provider>;
}

export function useExpenses() {
  return useContext(ExpenseContext);
}
